import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, MessagesSquare, Plus, Trash2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import { generateToolboxTalkWordDocument } from "@/lib/documentGenerator/basuWordGenerators";

interface Talk {
  id: string;
  topic: string;
  purpose: string | null;
  held_at: string | null;
  location: string | null;
  language: string;
  leader: string | null;
  companies: string | null;
  activities: string | null;
  incidents: string | null;
  decisions: string | null;
  status: string;
}

interface Participant {
  id: string;
  talk_id: string;
  person_name: string;
  company: string | null;
  signed: boolean;
  note: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  planerad: "Planerad",
  genomford: "Genomförd",
};

const TOPICS = [
  "Fallrisk och skyddsräcken",
  "Lyft och lastning",
  "Heta arbeten",
  "Damm och kvarts",
  "Ställning och tillträdesleder",
  "Trafik och transporter på plats",
  "Ordning och reda",
  "Ellyftrisk och tillfällig el",
];

export const ToolboxTalks: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [talks, setTalks] = useState<Talk[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [{ data }, { data: parts }, { data: proj }] = await Promise.all([
      supabase
        .from("toolbox_talks")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false }),
      supabase.from("toolbox_talk_participants").select("*").eq("project_id", projectId),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    setProjectName(proj?.name ?? "");
    setTalks((data as unknown as Talk[]) || []);
    setParticipants((parts as unknown as Participant[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const add = async () => {
    const { error } = await supabase.from("toolbox_talks").insert({
      project_id: projectId,
      topic: "Ny arbetsmiljödialog",
      held_at: new Date().toISOString().slice(0, 10),
    });
    if (error) return toast.error("Kunde inte skapa");
    load();
  };

  const update = async (id: string, patch: Partial<Talk>) => {
    setTalks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    const { error } = await supabase.from("toolbox_talks").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("toolbox_talks").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort");
    setTalks((prev) => prev.filter((t) => t.id !== id));
  };

  const addParticipant = async (talkId: string) => {
    const { error } = await supabase.from("toolbox_talk_participants").insert({
      talk_id: talkId,
      project_id: projectId,
      person_name: "Ny deltagare",
    });
    if (error) return toast.error("Kunde inte lägga till deltagare");
    load();
  };

  const updateParticipant = async (id: string, patch: Partial<Participant>) => {
    setParticipants((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    const { error } = await supabase.from("toolbox_talk_participants").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const removeParticipant = async (id: string) => {
    await supabase.from("toolbox_talk_participants").delete().eq("id", id);
    setParticipants((prev) => prev.filter((p) => p.id !== id));
  };

  const exportWord = async (talk: Talk) => {
    try {
      await generateToolboxTalkWordDocument(
        talk,
        participants.filter((p) => p.talk_id === talk.id),
        projectName
      );
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="45"
        exportHistory={{ documentType: "toolbox_talk" }}
        title="Toolbox talk – arbetsmiljödialog"
        description="Korta genomgångar av aktuella risker med de som utför arbetet. Deltagarlista och beslut dokumenteras och kan exporteras."
        legalRef="AFS 2023:1 · AFS 2023:3 kap. 4"
        icon={MessagesSquare}
        meta={<Badge variant="outline">{talks.length} genomgångar</Badge>}
        actions={
          <Button size="sm" variant="outline" onClick={add}>
            <Plus className="mr-1 h-4 w-4" /> Ny genomgång
          </Button>
        }
      />

      {!talks.length ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Inga genomgångar registrerade. Håll en kort dialog varje vecka om det riskfyllda momentet som
            är aktuellt.
          </CardContent>
        </Card>
      ) : (
        talks.map((talk) => {
          const rows = participants.filter((p) => p.talk_id === talk.id);
          return (
            <Card key={talk.id}>
              <CardContent className="space-y-4 pt-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Input
                    className="max-w-md font-medium"
                    defaultValue={talk.topic}
                    onBlur={(e) => update(talk.id, { topic: e.target.value })}
                  />
                  <div className="flex items-center gap-2">
                    <Select value={talk.status} onValueChange={(v) => update(talk.id, { status: v })}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([k, label]) => (
                          <SelectItem key={k} value={k}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => exportWord(talk)}>
                      <Download className="mr-1 h-4 w-4" /> Word
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Ta bort genomgången"
                      onClick={() => remove(talk.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      defaultValue={talk.held_at ?? ""}
                      onBlur={(e) => update(talk.id, { held_at: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Plats</Label>
                    <Input
                      defaultValue={talk.location ?? ""}
                      onBlur={(e) => update(talk.id, { location: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Språk</Label>
                    <Select value={talk.language} onValueChange={(v) => update(talk.id, { language: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sv">Svenska</SelectItem>
                        <SelectItem value="en">Engelska</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ledare</Label>
                    <Input
                      defaultValue={talk.leader ?? ""}
                      onBlur={(e) => update(talk.id, { leader: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Företag på plats</Label>
                    <Input
                      defaultValue={talk.companies ?? ""}
                      onBlur={(e) => update(talk.id, { companies: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Genomgång</Label>
                  <Textarea
                    rows={3}
                    placeholder={`Förslag på ämnen: ${TOPICS.slice(0, 3).join(", ")}`}
                    defaultValue={talk.activities ?? ""}
                    onBlur={(e) => update(talk.id, { activities: e.target.value })}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Tillbud och observationer</Label>
                    <Textarea
                      rows={2}
                      defaultValue={talk.incidents ?? ""}
                      onBlur={(e) => update(talk.id, { incidents: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Beslut och åtgärder</Label>
                    <Textarea
                      rows={2}
                      defaultValue={talk.decisions ?? ""}
                      onBlur={(e) => update(talk.id, { decisions: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Deltagare ({rows.length})</h3>
                    <Button size="sm" variant="ghost" onClick={() => addParticipant(talk.id)}>
                      <Plus className="mr-1 h-4 w-4" /> Lägg till
                    </Button>
                  </div>
                  {rows.map((p) => (
                    <div key={p.id} className="grid items-center gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
                      <Input
                        defaultValue={p.person_name}
                        onBlur={(e) => updateParticipant(p.id, { person_name: e.target.value })}
                      />
                      <Input
                        placeholder="Företag"
                        defaultValue={p.company ?? ""}
                        onBlur={(e) => updateParticipant(p.id, { company: e.target.value })}
                      />
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`sign-${p.id}`}
                          checked={p.signed}
                          onCheckedChange={(v) => updateParticipant(p.id, { signed: v })}
                        />
                        <Label htmlFor={`sign-${p.id}`} className="text-xs">
                          Signerat
                        </Label>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Ta bort deltagaren"
                        onClick={() => removeParticipant(p.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default ToolboxTalks;
